/**
 * Browser-seitige Bildkomprimierung für Uploads.
 *
 * Hintergrund: Die Anthropic-API erlaubt pro Bild max 5 MiB. Moderne Handy-
 * Fotos (12–48 MP) liegen oft bei 6–15 MB und führen zu 400er-Fehlern wie
 * "image exceeds 5 MB maximum". Wir skalieren deshalb VOR dem Upload auf
 * eine zumutbare Maximalkante herunter und re-encoden ggf. als JPEG. Wenn
 * das Bild bereits klein genug ist und ein unterstütztes Format hat, wird
 * es 1:1 weitergegeben (kein Qualitätsverlust ohne Grund).
 */

const DEFAULT_MAX_BYTES = 4 * 1024 * 1024; // 4 MiB Sicherheitspuffer < 5 MiB API-Limit
const DEFAULT_MAX_EDGE = 2400;
const DEFAULT_QUALITY = 0.85;
const MIN_EDGE = 800;
const MIN_QUALITY = 0.5;

const SAFE_PASSTHROUGH_MIMES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export interface CompressedImage {
  /** Base64 (ohne data:-Prefix). */
  data: string;
  /** MIME-Type des Ergebnisses (kann sich vom Eingangs-MIME unterscheiden). */
  mimeType: string;
  /** Größe der Roh-Bytes nach (eventueller) Komprimierung. */
  fileSize: number;
  /** True, wenn das Bild tatsächlich neu codiert wurde. */
  recompressed: boolean;
}

export interface CompressOptions {
  maxBytes?: number;
  maxEdge?: number;
  quality?: number;
}

function readAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("FileReader-Fehler"));
    reader.readAsArrayBuffer(file);
  });
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  // In 32 KiB-Blöcken konvertieren, damit String.fromCharCode keinen
  // Stack-Overflow bei großen Bildern produziert.
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const sub = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, Array.from(sub));
  }
  return btoa(binary);
}

function blobToBase64(blob: Blob): Promise<string> {
  return readAsArrayBuffer(blob).then(arrayBufferToBase64);
}

async function loadImageBitmap(file: File): Promise<{
  width: number;
  height: number;
  draw: (canvas: HTMLCanvasElement, w: number, h: number) => void;
  close: () => void;
}> {
  // Bevorzugt createImageBitmap (schneller, off-thread), Fallback auf <img>.
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (canvas, w, h) => {
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas-Kontext nicht verfügbar");
          ctx.drawImage(bitmap, 0, 0, w, h);
        },
        close: () => bitmap.close(),
      };
    } catch {
      // weiter zum <img>-Fallback
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
      el.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (canvas, w, h) => {
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas-Kontext nicht verfügbar");
        ctx.drawImage(img, 0, 0, w, h);
      },
      close: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error("Canvas-Export fehlgeschlagen"));
      },
      type,
      quality,
    );
  });
}

/**
 * Komprimiert eine Datei so, dass die Roh-Größe unter `maxBytes` liegt.
 * Bilder werden auf max `maxEdge` skaliert, dann als JPEG mit absteigender
 * Qualität neu encodiert. Nicht-Bild-Dateien werden 1:1 zurückgegeben.
 */
export async function fileToCompressedBase64(
  file: File,
  opts?: CompressOptions,
): Promise<CompressedImage> {
  const maxBytes = opts?.maxBytes ?? DEFAULT_MAX_BYTES;
  const maxEdge = opts?.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts?.quality ?? DEFAULT_QUALITY;

  const inputMime = (file.type || "").toLowerCase();
  const normalizedInputMime = inputMime === "image/jpg" ? "image/jpeg" : inputMime;
  const isImage = normalizedInputMime.startsWith("image/");

  // Passthrough: passende Größe + akzeptierter MIME → kein Re-Encoding.
  if (isImage && file.size <= maxBytes && SAFE_PASSTHROUGH_MIMES.has(normalizedInputMime)) {
    const data = await blobToBase64(file);
    return { data, mimeType: normalizedInputMime, fileSize: file.size, recompressed: false };
  }

  // Nicht-Bild → unverändert weitergeben (Backend entscheidet, was es kann).
  if (!isImage) {
    const data = await blobToBase64(file);
    return {
      data,
      mimeType: normalizedInputMime || "application/octet-stream",
      fileSize: file.size,
      recompressed: false,
    };
  }

  const source = await loadImageBitmap(file);
  try {
    let edge = maxEdge;
    let q = quality;
    let lastBlob: Blob | null = null;
    // Bis zu 5 Iterationen, jeweils Größe ODER Qualität reduzieren.
    for (let attempt = 0; attempt < 5; attempt++) {
      const scale = Math.min(1, edge / Math.max(source.width, source.height));
      const targetW = Math.max(1, Math.round(source.width * scale));
      const targetH = Math.max(1, Math.round(source.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      source.draw(canvas, targetW, targetH);
      const blob = await canvasToBlob(canvas, "image/jpeg", q);
      lastBlob = blob;
      if (blob.size <= maxBytes) {
        const data = await blobToBase64(blob);
        return { data, mimeType: "image/jpeg", fileSize: blob.size, recompressed: true };
      }
      // Erst Qualität reduzieren, dann Kantenlänge.
      if (q > MIN_QUALITY + 0.05) {
        q = Math.max(MIN_QUALITY, q - 0.15);
      } else if (edge > MIN_EDGE) {
        edge = Math.max(MIN_EDGE, Math.round(edge * 0.75));
        q = quality; // nach Skalierung Qualität wieder hoch
      } else {
        break;
      }
    }
    if (!lastBlob) {
      throw new Error("Bild konnte nicht komprimiert werden.");
    }
    // Letzter Versuch ist immer noch zu groß → trotzdem zurückgeben (besser
    // als gar nichts, der Server-Call wird mit klarer Meldung scheitern).
    const data = await blobToBase64(lastBlob);
    return { data, mimeType: "image/jpeg", fileSize: lastBlob.size, recompressed: true };
  } finally {
    source.close();
  }
}
